import { readFileAsURL } from "@certn/cascadia-react";
import { useSyncExternalStore } from "react";

import { BRAND_ASSET_BUCKET_DIRECTORY } from "./sanitizeBrandAssetFileName";

const STORAGE_PREFIX = "brand-asset:";

let objectUrls = new Map<string, string>();
const pendingReads = new Map<string, symbol>();
const listeners = new Set<() => void>();

function cancelPendingRead(normalized: string) {
  pendingReads.delete(normalized);
}

function update(mutate: (next: Map<string, string>) => void) {
  const next = new Map(objectUrls);
  mutate(next);
  objectUrls = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function assetKey(value: string) {
  const index = value.indexOf(BRAND_ASSET_BUCKET_DIRECTORY);
  const key = index === -1 ? value : value.slice(index);
  try {
    return decodeURIComponent(key);
  } catch {
    return key;
  }
}

function storageKey(key: string) {
  return `${STORAGE_PREFIX}${key}`;
}

function readStoredUrl(key: string) {
  try {
    return sessionStorage.getItem(storageKey(key)) ?? undefined;
  } catch {
    return undefined;
  }
}

export function rememberBrandAssetObjectUrl(
  key: string | null | undefined,
  url: string | null,
  file?: File
) {
  if (!key) {
    return;
  }
  const normalized = assetKey(key);
  cancelPendingRead(normalized);

  if (!url) {
    try {
      sessionStorage.removeItem(storageKey(normalized));
    } catch {
      // Storage can be unavailable in private browsing.
    }
    update((next) => next.delete(normalized));
    return;
  }

  update((next) => next.set(normalized, url));

  if (!file) {
    return;
  }

  const readToken = Symbol();
  pendingReads.set(normalized, readToken);

  void readFileAsURL(file).then((dataUrl) => {
    if (pendingReads.get(normalized) !== readToken || !dataUrl) {
      return;
    }
    pendingReads.delete(normalized);
    try {
      sessionStorage.setItem(storageKey(normalized), dataUrl);
    } catch {
      // Ignore quota failures. The in-memory URL still covers this session.
    }
    update((next) => next.set(normalized, dataUrl));
  });
}

export function brandAssetDisplayUrl(
  remoteUrl?: string | null,
  cache: ReadonlyMap<string, string> = objectUrls
) {
  if (!remoteUrl) {
    return undefined;
  }
  const key = assetKey(remoteUrl);
  return cache.get(key) ?? readStoredUrl(key) ?? remoteUrl;
}

export function useBrandAssetObjectUrls() {
  return useSyncExternalStore(
    subscribe,
    () => objectUrls,
    () => objectUrls
  );
}
